/**
 * Base → Review (`/solutions/review`): the statement / data checker.
 *
 * Give it a draft (a submission section, label text, a CSR summary); it
 * extracts every factual statement and number and checks each against the
 * chosen collections — supported, contradicted (both values shown) or not
 * found. Q&A answers questions; Review checks assertions.
 *
 * Left: the caller's Review runs. Right: a new run's form, or the selected
 * run's live status and, once finished, its result and PDF report.
 */

import { useCallback, useState } from "react";
import { Button, Card, Container, Grid, Group, Stack, Text, Title } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import {
  AdvisoryBanner,
  ReportDownloadButton,
  RunList,
  RunStatus,
} from "@/features/solutions/shared";
import type { RunDetailOut } from "@/shared/api/solutionRuns";
import { ReviewForm } from "./ReviewForm";
import { ReviewResultView } from "./ReviewResultView";
import { asReviewResult } from "./types";

function RunView({ runId }: { runId: string }) {
  const [run, setRun] = useState<RunDetailOut | null>(null);
  const onUpdate = useCallback((r: RunDetailOut) => setRun(r), []);
  const result = run?.status === "succeeded" ? asReviewResult(run.result) : null;
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={4}>{run?.name ?? "Run"}</Title>
        {run?.status === "succeeded" && <ReportDownloadButton run={run} />}
      </Group>
      <RunStatus runId={runId} onUpdate={onUpdate} />
      {result && (
        <>
          <AdvisoryBanner />
          <Text size="sm" c="dimmed">
            Draft: {result.draft.display_name}
          </Text>
          <ReviewResultView result={result} />
        </>
      )}
    </Stack>
  );
}

export function ReviewPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <Container fluid py="lg">
      <Stack gap="md">
        <div>
          <Title order={2}>Review</Title>
          <Text c="dimmed">
            Check every statement and number in a draft against your source documents.
          </Text>
        </div>
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 4, xl: 3 }}>
            <Card withBorder radius="md" p="md">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600}>Your reviews</Text>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => setSelected(null)}
                  >
                    New review
                  </Button>
                </Group>
                <RunList
                  kind="review"
                  selectedRunId={selected}
                  onSelect={(r) => setSelected(r.run_id)}
                  refreshKey={refreshKey}
                  emptyText="No reviews yet."
                />
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 8, xl: 9 }}>
            <Card withBorder radius="md" p="md">
              {selected ? (
                <RunView key={selected} runId={selected} />
              ) : (
                <ReviewForm
                  onCreated={(run) => {
                    setRefreshKey((k) => k + 1);
                    setSelected(run.run_id);
                  }}
                />
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </Container>
  );
}
